import React, { useMemo, useState } from "react";
import { CaretUp, CaretDown } from "@phosphor-icons/react";

// Generic sortable table. columns: [{key, label, render?, align?, sortValue?}]
export function SortableTable({ columns, rows, footer, testid, rowKey, onRowClick, initialSort }) {
  const [sort, setSort] = useState(initialSort || { key: columns[0].key, dir: "asc" });
  const sorted = useMemo(() => {
    const col = columns.find(c => c.key === sort.key);
    const val = (r) => (col?.sortValue ? col.sortValue(r) : r[sort.key]);
    return [...rows].sort((a, b) => {
      const x = val(a), y = val(b);
      const c = typeof x === "number" && typeof y === "number" ? x - y : String(x ?? "").localeCompare(String(y ?? ""));
      return sort.dir === "asc" ? c : -c;
    });
  }, [rows, sort, columns]);
  const toggle = (k) => setSort(s => ({ key: k, dir: s.key === k && s.dir === "desc" ? "asc" : "desc" }));

  return (
    <div className="overflow-x-auto bg-white border border-[#E5E2D9] rounded-md" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.16em] text-[#5C5A55]">
            {columns.map(c => (
              <th key={c.key} onClick={() => toggle(c.key)} data-testid={`${testid}-sort-${c.key}`}
                  className={`px-3 py-2.5 cursor-pointer select-none whitespace-nowrap hover:text-[#1A1A1A] ${c.align === "right" ? "text-right" : "text-left"}`}>
                {c.label}
                {sort.key === c.key && (sort.dir === "asc" ? <CaretUp size={9} className="inline ml-1"/> : <CaretDown size={9} className="inline ml-1"/>)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-10 text-center text-[#8A8780]">No data for these filters</td></tr>}
          {sorted.map((r, i) => (
            <tr key={rowKey ? rowKey(r) : i} onClick={onRowClick ? () => onRowClick(r) : undefined}
                data-testid={`${testid}-row-${rowKey ? rowKey(r) : i}`}
                className={`border-t border-[#F0EDE4] ${onRowClick ? "cursor-pointer hover:bg-[#FBF6EF]" : ""}`}>
              {columns.map(c => (
                <td key={c.key} className={`px-3 py-2 whitespace-nowrap ${c.align === "right" ? "text-right font-mono text-[12px]" : ""}`}>
                  {c.render ? c.render(r) : r[c.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer && (
          <tfoot>
            <tr className="border-t-2 border-[#1A1A1A] bg-[#F7F6F2] font-semibold" data-testid={`${testid}-totals`}>
              {columns.map(c => (
                <td key={c.key} className={`px-3 py-2 whitespace-nowrap ${c.align === "right" ? "text-right font-mono text-[12px]" : ""}`}>
                  {c.render && footer[c.key] !== undefined && c.key !== columns[0].key ? c.render(footer) : footer[c.key] ?? ""}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
